import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { LocationProvider } from './LocationContext';
import { checkSafariCompatibility, addSafariPolyfills } from './utils/safariCheck';

// Debug logging
console.log('index.js starting...');

// Check Safari compatibility
const safariCheck = checkSafariCompatibility();
console.log('Safari compatibility check:', safariCheck);

// Add polyfills if needed
addSafariPolyfills();

// Global error handlers
window.onerror = function(msg, url, lineNo, columnNo, error) {
    console.error('Window error:', msg, url, lineNo, columnNo, error);
    const errorDiv = document.getElementById('error-display');
    if (errorDiv) {
        errorDiv.innerHTML = `
            <h3>JavaScript Error:</h3>
            <p><strong>Message:</strong> ${msg}</p>
            <p><strong>URL:</strong> ${url}</p>
            <p><strong>Line:</strong> ${lineNo}, Column: ${columnNo}</p>
            <pre>${error?.stack || 'No stack trace'}</pre>
        `;
        errorDiv.style.display = 'block';
    }
    return true;
};

window.addEventListener('unhandledrejection', function(event) {
    console.error('Unhandled promise rejection:', event.reason);
    const errorDiv = document.getElementById('error-display');
    if (errorDiv) {
        errorDiv.innerHTML = `
            <h3>Unhandled Promise Rejection:</h3>
            <pre>${event.reason}</pre>
        `;
        errorDiv.style.display = 'block';
    }
});

// Suppress ResizeObserver loop errors
const debounce = (callback, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => callback(...args), delay);
    };
};

const resizeObserverErr = debounce(() => {
    console.warn('ResizeObserver loop completed with undelivered notifications.');
}, 100);

window.addEventListener('error', (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
        resizeObserverErr();
    }
});

console.log('Creating React root...');

try {
    const rootElement = document.getElementById('root');
    if (!rootElement) {
        throw new Error('Root element not found! Make sure there is a div with id="root" in index.html');
    }
    
    console.log('Root element found, creating React root...');
    
    // For Safari compatibility, use a fallback rendering method
    if (safariCheck.isIOS && safariCheck.isSafari) {
        console.log('Using iOS Safari compatible rendering...');
        
        // Try React 18 createRoot first
        try {
            const root = ReactDOM.createRoot(rootElement);
            console.log('React 18 createRoot successful on Safari');
            
            root.render(
                <React.StrictMode>
                    <LocationProvider>
                        <App />
                    </LocationProvider>
                </React.StrictMode>
            );
            
            console.log('App rendered successfully with React 18');
        } catch (e) {
            console.error('React 18 createRoot failed, falling back to legacy render:', e);
            
            // Fallback to legacy render for older Safari
            ReactDOM.render(
                <React.StrictMode>
                    <LocationProvider>
                        <App />
                    </LocationProvider>
                </React.StrictMode>,
                rootElement
            );
            
            console.log('App rendered successfully with legacy ReactDOM.render');
        }
    } else {
        // Standard React 18 rendering for non-Safari browsers
        const root = ReactDOM.createRoot(rootElement);
        
        console.log('Rendering app...');
        root.render(
            <LocationProvider>
                <App />
            </LocationProvider>
        );
        
        console.log('App rendered successfully');
    }
} catch (error) {
    console.error('Failed to render app:', error);
    document.body.innerHTML = `
        <div style="padding: 20px; font-family: Arial;">
            <h1>Failed to load application</h1>
            <h3>Error:</h3>
            <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error.message}</pre>
            <h3>Stack:</h3>
            <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error.stack}</pre>
        </div>
    `;
}

// Service worker policy: the dashboard does NOT register a custom
// service worker. Push notifications come through Firebase's own
// `firebase-messaging-sw.js`, registered separately by the Firebase SDK.
//
// `/service-worker.js` is intentionally a self-unregistering kill-switch
// (see public/service-worker.js) — once every active user has hit the
// site once, all stale registrations are cleared. We still need the
// file in the build so old browsers can fetch + activate the stub.
//
// Why we removed the custom SW entirely: it cached `index.html` against
// hashed CRA asset URLs, so every deploy left old browsers serving an
// index.html that referenced JS/CSS files that no longer existed. The
// dashboard worked in incognito (no SW) but broke in normal browsers.
